import type {Margins, UnitSize, AutoGridCalculationConfig, Rect, Pair} from '@wix/document-services-types'
import type {
    AutoGridCalculationInternalResults,
    ChildLayoutEntry,
    ComponentCalculationInfo,
    GridRowsWithLastRow,
    InitialGridCalculationResults,
    LastRowInfo,
    RowInfo
} from './types'

const inRange = (x: number, start: number, end: number): boolean => x >= start && x < end
const getBottom = (rect: Rect): number => rect.y + rect.height
const getMidY = (rect: Rect): number => rect.y + rect.height / 2
const isBoundedY = (boundedRect: Rect, boundingRect: Rect): boolean => {
    return (
        boundedRect.y > boundingRect.y &&
        ((getBottom(boundedRect) <= getBottom(boundingRect) && boundedRect.height !== 0) ||
            (boundedRect.y < getBottom(boundingRect) && boundedRect.height === 0))
    )
}
const isSameTopAndZeroHeight = (componentRect: Rect, siblingRect: Rect): boolean =>
    componentRect.y === siblingRect.y && componentRect.height === 0 && siblingRect.height === 0
const hasTopTopAnchor = (compRect: Rect, siblingRect: Rect): boolean =>
    isBoundedY(compRect, siblingRect) ||
    isSameTopAndZeroHeight(compRect, siblingRect) ||
    inRange(compRect.y, siblingRect.y, getMidY(siblingRect))
const hasBottomTopAnchor = (compRect: Rect, siblingRect: Rect): boolean =>
    !isBoundedY(compRect, siblingRect) && getMidY(siblingRect) <= compRect.y

const createSPXUnitSize = (value: number): UnitSize => ({
    value,
    type: 'spx'
})

const createPercentageUnitSize = (value: number): UnitSize => ({
    value,
    type: 'percentage'
})

const createPartialLayoutForGridItem = (
    row: number,
    marginTop: number,
    leftMargin: number,
    containerWidth: number
): ChildLayoutEntry => ({
    itemLayout: {
        alignSelf: 'start',
        justifySelf: 'start',
        margins: {
            left: createPercentageUnitSize((leftMargin / containerWidth) * 100),
            right: createPercentageUnitSize(0),
            top: createSPXUnitSize(marginTop)
        },
        gridArea: {
            rowStart: row,
            rowEnd: row + 1,
            columnStart: 1,
            columnEnd: 2
        },
        type: 'GridItemLayout'
    }
})

const createEmptyGridContainerLayout = (
    {gridMargins, minHeight = 0}: {gridMargins?: Margins; minHeight?: number} = {minHeight: 0}
): AutoGridCalculationInternalResults['containerLayout'] => ({
    containerLayout: {
        rows: [
            {
                type: 'MinMaxSize',
                min: {type: 'spx', value: minHeight},
                max: {type: 'auto'}
            }
        ],
        columns: [{type: 'fr', value: 1}],
        ...(gridMargins && {gridMargins}),
        padding: {
            left: {
                value: 0,
                type: 'spx'
            },
            right: {
                value: 0,
                type: 'spx'
            },
            top: {
                value: 0,
                type: 'spx'
            },
            bottom: {
                value: 0,
                type: 'spx'
            }
        },
        type: 'GridContainerLayout'
    }
})

const createRowInfo = (
    index: number,
    y: number,
    firstComponentId: string,
    compLayoutEndsFirst: Rect
): Pair<number, RowInfo> => [
    index,
    {
        y,
        firstComponentId,
        compLayoutEndsFirst,
        components: {
            starting: [firstComponentId],
            ending: []
        }
    }
]

const createLastRowInfo = (index: number): Pair<number, LastRowInfo> => [
    index,
    {
        components: {
            starting: [],
            ending: []
        }
    }
]

const preprocessInput = (
    childIdToRectMap: Record<string, Rect>,
    sortingFunction: (ComponentA: ComponentCalculationInfo, ComponentB: ComponentCalculationInfo) => number
): ComponentCalculationInfo[] => {
    return Object.entries(childIdToRectMap)
        .map(([componentId, layout]) => ({
            componentId,
            layout
        }))
        .sort(sortingFunction)
}

const createInitialGrid = (
    {width: containerWidth}: Rect,
    childIdToRectMap: Record<string, Rect>,
    {overflowGrid}: AutoGridCalculationConfig
): InitialGridCalculationResults => {
    const compRectsTopAsc = preprocessInput(
        childIdToRectMap,
        (componentA: ComponentCalculationInfo, componentB: ComponentCalculationInfo) =>
            componentA.layout.y - componentB.layout.y
    )
    const [highestComponent, ...childrenRects]: ComponentCalculationInfo[] = compRectsTopAsc
    let [currentRowIndex, currentRowInfo] = createRowInfo(
        1,
        Math.min(0, !overflowGrid ? Math.max(0, highestComponent.layout.y) : highestComponent.layout.y),
        highestComponent.componentId,
        highestComponent.layout
    )
    const rows: Pair<number, RowInfo>[] = [[currentRowIndex, currentRowInfo]]
    const containerLayout = createEmptyGridContainerLayout({
        gridMargins: overflowGrid ? {top: createSPXUnitSize(currentRowInfo.y)} : undefined
    })
    const componentsLayouts: Record<string, ChildLayoutEntry> = {
        [highestComponent.componentId]: createPartialLayoutForGridItem(
            currentRowIndex,
            overflowGrid ? Math.max(0, highestComponent.layout.y) : highestComponent.layout.y,
            highestComponent.layout.x,
            containerWidth
        )
    }

    for (const {componentId, layout} of childrenRects) {
        let marginTop = 0
        const shouldCreateANewRow =
            !hasTopTopAnchor(layout, currentRowInfo.compLayoutEndsFirst) && !(!overflowGrid && layout.y < 0)

        if (shouldCreateANewRow) {
            const rowInfoEntry = ([currentRowIndex, currentRowInfo] = createRowInfo(
                currentRowIndex + 1,
                layout.y,
                componentId,
                layout
            ))
            rows.push(rowInfoEntry)
        } else {
            marginTop = layout.y - currentRowInfo.y
            currentRowInfo.components.starting.push(componentId)
        }

        componentsLayouts[componentId] = createPartialLayoutForGridItem(
            currentRowIndex,
            marginTop,
            layout.x,
            containerWidth
        )
        currentRowInfo.compLayoutEndsFirst =
            getBottom(currentRowInfo.compLayoutEndsFirst) > getBottom(layout)
                ? layout
                : currentRowInfo.compLayoutEndsFirst
    }

    return {
        containerLayout,
        componentsLayouts,
        rows: [...rows, createLastRowInfo(currentRowIndex + 1)]
    }
}

const calculateBottomMarginsForPushingComponents = (
    childIdToRectMap: Record<string, Rect>,
    pushedComponentId: string,
    pushingComponentsIds: string[],
    componentsLayouts: Record<string, ChildLayoutEntry>
): void => {
    const pushedComponentRect = childIdToRectMap[pushedComponentId]

    for (const pushingComponentId of pushingComponentsIds) {
        const pushingComponentRect = childIdToRectMap[pushingComponentId]
        const hasAnchor = hasBottomTopAnchor(pushedComponentRect, pushingComponentRect)

        if (hasAnchor && !componentsLayouts[pushingComponentId].itemLayout.margins!.bottom) {
            componentsLayouts[pushingComponentId].itemLayout.margins!.bottom = createSPXUnitSize(
                pushedComponentRect.y - getBottom(pushingComponentRect)
            )
        }
    }
}

const calculateMarginsForPushingComponents = (
    childIdToRectMap: Record<string, Rect>,
    childIdToLayoutMap: Record<string, ChildLayoutEntry>,
    rows: GridRowsWithLastRow
): void => {
    for (let i = 1; i < rows.length - 1; i++) {
        const [, currentRowInfo] = rows[i] as Pair<number, RowInfo>
        const [, previousRowInfo] = rows[i - 1] as Pair<number, RowInfo>

        calculateBottomMarginsForPushingComponents(
            childIdToRectMap,
            currentRowInfo.firstComponentId,
            previousRowInfo.components.starting,
            childIdToLayoutMap
        )
    }
}

const calculateEndRowsBasedOnBottom = (
    {height: containerHeight}: Rect,
    childIdToRectMap: Record<string, Rect>,
    componentsLayouts: Record<string, ChildLayoutEntry>,
    containerLayout: AutoGridCalculationInternalResults['containerLayout'],
    _rows: GridRowsWithLastRow,
    {isFitToContentContainer, overflowGrid}: AutoGridCalculationConfig
): void => {
    const rows: Pair<number, RowInfo>[] = _rows.slice(0, -1) as Pair<number, RowInfo>[]
    const indexToRowInfo = Object.fromEntries(_rows)
    const childrenRectsByBottomDesc = preprocessInput(
        childIdToRectMap,
        (componentA: ComponentCalculationInfo, componentB: ComponentCalculationInfo) =>
            getBottom(componentB.layout) - getBottom(componentA.layout)
    )
    const lowestComponent = childrenRectsByBottomDesc[0]
    const getDistanceFromParentBottom = (layout: Rect): number =>
        containerHeight && !isFitToContentContainer ? containerHeight - (layout.y + layout.height) : 0
    const getRowY = ([, rowInfo]: Pair<number, RowInfo>) => childIdToRectMap[rowInfo.firstComponentId].y

    if (overflowGrid) {
        containerLayout.containerLayout.gridMargins!.bottom = createSPXUnitSize(0)
    }

    if (!componentsLayouts[lowestComponent.componentId].itemLayout.margins!.bottom) {
        const distanceFromParentBottom = getDistanceFromParentBottom(lowestComponent.layout)
        const lowestCompMarginBottom = !overflowGrid ? distanceFromParentBottom : Math.max(0, distanceFromParentBottom)
        componentsLayouts[lowestComponent.componentId].itemLayout.margins!.bottom =
            createSPXUnitSize(lowestCompMarginBottom)

        if (overflowGrid) {
            containerLayout.containerLayout.gridMargins!.bottom = createSPXUnitSize(
                Math.min(0, distanceFromParentBottom)
            )
        }
    }

    const totalRows = rows.length

    for (const {componentId, layout} of childrenRectsByBottomDesc) {
        const marginBottomObject = componentsLayouts[componentId].itemLayout.margins?.bottom as UnitSize | undefined
        const marginBottom = marginBottomObject ? Math.min(marginBottomObject.value, 0) : 0

        while (
            getBottom(layout) + marginBottom <= getRowY(rows[rows.length - 1]) &&
            componentsLayouts[componentId].itemLayout.gridArea.rowStart < rows[rows.length - 1][0]
        ) {
            rows.pop()
        }

        const isComponentEndsInLowestRow = rows.length === totalRows

        if (!marginBottomObject) {
            const distanceFromParentBottom = isComponentEndsInLowestRow ? getDistanceFromParentBottom(layout) : 0
            const marginBottomValue = !overflowGrid ? distanceFromParentBottom : Math.max(0, distanceFromParentBottom)
            componentsLayouts[componentId].itemLayout.margins!.bottom = createSPXUnitSize(marginBottomValue)
        }

        componentsLayouts[componentId].itemLayout.gridArea.rowEnd = rows[rows.length - 1][0] + 1
        indexToRowInfo[componentsLayouts[componentId].itemLayout.gridArea.rowEnd]!.components.ending.push(componentId)
    }
}

const addEmptyRows = (
    rows: GridRowsWithLastRow,
    componentsLayouts: InitialGridCalculationResults['componentsLayouts'],
    containerLayout: AutoGridCalculationInternalResults['containerLayout']
): void => {
    let currentRow = 1
    const getSpan = (componentId: string): number =>
        componentsLayouts[componentId].itemLayout.gridArea.rowEnd -
        componentsLayouts[componentId].itemLayout.gridArea.rowStart

    for (const [, rowInfo] of rows) {
        const {
            components: {starting, ending}
        } = rowInfo
        const sortedEndingComponentsBySpan = ending.sort(
            (componentA, componentB) => getSpan(componentA) - getSpan(componentB)
        )

        for (const [i, componentId] of sortedEndingComponentsBySpan.entries()) {
            if (i !== 0 && getSpan(componentId) > 1) {
                currentRow++
            }

            componentsLayouts[componentId].itemLayout.gridArea.rowEnd = currentRow
        }

        for (const componentId of starting) {
            componentsLayouts[componentId].itemLayout.gridArea.rowStart = currentRow
        }

        currentRow++
    }

    containerLayout.containerLayout.rows = Array(currentRow - 2).fill({type: 'minContent'})
}

export const calculateGridByRects = (
    containerRect: Rect,
    childIdToRectMap: Record<string, Rect>,
    config: AutoGridCalculationConfig = {overflowGrid: false, isFitToContentContainer: false}
): AutoGridCalculationInternalResults => {
    if (!childIdToRectMap || Object.keys(childIdToRectMap).length <= 0) {
        return {
            componentsLayouts: {},
            containerLayout: createEmptyGridContainerLayout({minHeight: containerRect.height})
        }
    }

    const {containerLayout, componentsLayouts, rows} = createInitialGrid(containerRect, childIdToRectMap, config)
    calculateMarginsForPushingComponents(childIdToRectMap, componentsLayouts, rows)
    calculateEndRowsBasedOnBottom(containerRect, childIdToRectMap, componentsLayouts, containerLayout, rows, config)
    addEmptyRows(rows, componentsLayouts, containerLayout)

    return {componentsLayouts, containerLayout}
}
